import { motion } from 'framer-motion';
import { X } from 'lucide-react';

import { useModal } from '../../../hooks/modalContext';

import { useEffect, useState } from 'react';
import { APP_ENV, getPaymentSummary, initiateCryptoSession } from '../../../../auth/_requests';
import { PaymentMethodType } from '../../../utils/types';
import { useAuth } from '../../../../auth/Auth';
import { showToast } from '../../../utils/showToast';
import payWithCrypto from '../../../../assets/pages/payment/paywithcrypto.svg';
import { ButtonSpinner } from '../../../UI/ButtonSpinner/ButtonSpinner';

interface CryptoPaymentSummary {
  message: string;
  data: {
    userId: string;
    tenantBuildingId: string;
    tenantPaymentProcessorId: string;
    rentAmount: number;
    fixedFee: number;
    percentageFee: number;
    percentageRate: number;
    amountPaid: number;
    processingFee: number;
    discountAmount: number;
  };
}

const PayWithCrypto: React.FC = () => {
  const { setModal, modalState } = useModal();
  const tenantBuildingId = modalState?.tenantBuildingId || '';
  const { auth } = useAuth();
  const [paymentSummary, setPaymentSummary] = useState<CryptoPaymentSummary | null>(null);
  const [cryptoSessionId, setCryptoSessionId] = useState<string>('');
  const [cryptoAmount, setCryptoAmount] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [widgetReady, setWidgetReady] = useState(false);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
    }).format(amount || 0);
  };

  useEffect(() => {
    const fetchPaymentSummary = async () => {
      try {
        const summary = await getPaymentSummary(
          auth?.data?.token,
          tenantBuildingId,
          undefined,
          PaymentMethodType.Cryptocurrency
        );

        setPaymentSummary(summary as unknown as CryptoPaymentSummary);
      } catch (error) {
        console.error('Error fetching payment summary:', error);
        showToast({
          message: 'Error fetching payment summary: ' + error,
          type: 'error',
        });
      }
    };
    fetchPaymentSummary();
  }, [auth, tenantBuildingId]);

  useEffect(() => {
    const handleCryptoSelection = async () => {
      if (!cryptoSessionId) {
        try {
          setLoading(true);
          const response = await initiateCryptoSession(auth?.data?.token, tenantBuildingId);
          setCryptoAmount(String(response.amountPaid));
          setCryptoSessionId(response.tenantPaymentSessionId);
        } catch (err) {
          return;
        } finally {
          setLoading(false);
        }
      }
    };
    handleCryptoSelection();
  }, [auth, tenantBuildingId]);

  useEffect(() => {
    if (cryptoSessionId) {
      const script = document.createElement('script');
      if (APP_ENV === 'production') {
        script.src = 'https://alt5pay.com/widget/alt5pay.js?v4';
      } else {
        script.src = 'https://digitalpaydev.com/widget/paydev.js?v4';
      }

      script.async = true;

      const replaceImageSrc = () => {
        let injectedImg: HTMLImageElement | null = null;
        if (APP_ENV === 'production') {
          injectedImg = document.querySelector<HTMLImageElement>(
            'img[src^="https://alt5pay.com/widget/3378b3c57e844f101bb86b884d55277b.svg"]'
          );
        } else {
          injectedImg = document.querySelector<HTMLImageElement>(
            'img[src^="https://digitalpaydev.com/widget/3378b3c57e844f101bb86b884d55277b.svg"]'
          );
        }

        if (injectedImg) {
          injectedImg.src = payWithCrypto; // assuming payWithCrypto is a URL string
        }
      };
      script.onload = () => {
        replaceImageSrc();
        setWidgetReady(true);
        const style = document.createElement('style');

        style.innerHTML = `
  .btncancelfull{
            display: inline-flex;
           align-items: center;
         justify-content: center;
          flex: 1 1 auto;
           position: relative;
           padding-left: 16px;
             padding-right: 16px;
             height: 52px;
             border-radius: 10px;
             background-color: #0055FF !important;
             color: var(--btn-text);
             font-size: 14px;
             letter-spacing: 0.63px;
             transition: all 0.2s ease-in-out;
           }
             .btnpay {
             display: inline-flex;
             align-items: center;
             justify-content: center;
             flex: 1 1 auto;
             position: relative;
             padding-left: 16px;
             padding-right: 16px;
             height: 52px;
             border-radius: 10px;
             background-color: #0055FF !important;
             color: var(--btn-text);
             font-size: 14px;
             letter-spacing: 0.63px;
             transition: all 0.2s ease-in-out;
           }
              .btncancel {
             display: inline-flex;
             align-items: center;
             justify-content: center;
             flex: 1 1 auto;
             position: relative;
             padding-left: 16px;
             padding-right: 16px;
             height: 52px;
             border-radius: 10px;
             background-color:rgba(225,229,241,0.6) !important;
             color: black !important;
             font-size: 14px;
             letter-spacing: 0.63px;
             transition: all 0.2s ease-in-out;
           }

             .pay-modal_top{
             background-color: #0055FF !important;
             color: white !important;
             border: none !important;
             }
             .paylogo{
             margin-top:-5px !important;
             margin-left:auto;
             margin-right:auto;}

             .loader{
             margin-left: 45% !important;
             }
         `;
        document.head.appendChild(style);
        const observer = new MutationObserver(() => {
          const canvas = document.getElementById('react-qrcode-logo');
          if (canvas) {
            setModal(null);
            canvas.style.height = '200px';
            canvas.style.width = '200px';
            canvas.style.display = 'block';
            canvas.style.margin = '20px auto';
            observer.disconnect();
          }
        });

        observer.observe(document.body, {
          childList: true,
          subtree: true,
        });
      };

      document.body.appendChild(script);

      return () => {
        document.body.removeChild(script);
        const existingStyle = document.head.querySelector('style[data-pay-style]');
        if (existingStyle) document.head.removeChild(existingStyle);
      };
    }
  }, [cryptoSessionId]);
  return (
    <motion.div
      className='w-[554px] bg-[#FFF] rounded-[12px] flex flex-col overflow-auto relative'
      initial='hidden'
      animate='visible'
      exit='exit'
    >
      <div className='flex justify-end p-4'>
        <X
          className='cursor-pointer text-gray-500 hover:text-gray-700'
          size={24}
          onClick={() => setModal(null)}
        />
      </div>
      <div className='flex flex-col gap-4 px-10 pb-8 '>
        <span className='text-main text-[20px]'>Pay with crypto</span>
        <div className='relative w-full flex items-center'>
          <div className='absolute bottom-1 left-1/2 translate-x-[-50%] w-[80%] h-[60px] rounded-[20px] shadow-[0px_12px_50px_0px_#A6B1CB] z-0 pointer-events-none'></div>
          <div className='relative z-5 w-full rounded-[20px] border-2 border-[#E9EDF9] bg-[linear-gradient(180deg,_#FFF_-13.57%,_#F2F4F9_113.63%)] px-[20px] py-[32px]'>
            <div className='flex flex-col gap-3'>
              <div className='flex justify-between w-full items-center'>
                <span className='text-light/70 text-base'>Rent amount</span>
                <span className='text-main font-semibold'>
                  {formatCurrency(paymentSummary?.data?.rentAmount || 0)}
                </span>
              </div>
              <div className='flex justify-between w-full items-center'>
                <span className='text-light/70 text-base'>
                  {' '}
                  Processing fee{' '}
                  {paymentSummary?.data?.processingFee ? (
                    <span>
                      {(() => {
                        const fixed = paymentSummary?.data?.fixedFee;
                        const percentage = paymentSummary?.data?.percentageFee;
                        const percentageRate = paymentSummary?.data?.percentageRate;

                        if (fixed && fixed !== 0 && percentage && percentage !== 0) {
                          return ` ( ${formatCurrency(
                            fixed
                          )} + ${percentageRate}%: ${formatCurrency(percentage)})`;
                        } else if (fixed && fixed !== 0) {
                          return `(${formatCurrency(fixed)})`;
                        } else if (percentage && percentage !== 0) {
                          return `(${percentageRate}%: ${formatCurrency(percentage)})`;
                        }
                        return null;
                      })()}
                    </span>
                  ) : (
                    ''
                  )}
                </span>
                <span className='text-main font-semibold'>
                  {paymentSummary?.data?.processingFee !== 0
                    ? formatCurrency(paymentSummary?.data?.processingFee || 0)
                    : formatCurrency(0)}
                </span>
              </div>
              {Number(paymentSummary?.data?.discountAmount) > 0 && (
                <div className='flex justify-between w-full items-center'>
                  <span className='text-light/70 text-base'>Rent credit</span>
                  <span className='text-success font-semibold'>
                    {formatCurrency(paymentSummary?.data?.discountAmount || 0)}
                  </span>
                </div>
              )}
              <div className='flex justify-between w-full items-center'>
                <span className='text-light/70 text-base'>Total amount</span>
                <span className='text-primary font-semibold'>
                  {paymentSummary?.data?.amountPaid
                    ? formatCurrency(paymentSummary?.data?.amountPaid)
                    : formatCurrency(paymentSummary?.data?.rentAmount || 0)}
                </span>
              </div>
            </div>
          </div>
        </div>
        {Number(paymentSummary?.data?.discountAmount) > 0 && (
          <div className='mt-5 flex flex-col px-6 py-8 rounded-[12px] bg-[rgba(33,81,236,0.06)]'>
            <span className='text-primary text-base font-bold'>
              You have ${paymentSummary?.data?.discountAmount} rent credit!!
            </span>
            <ul className='text-primary text-sm list-disc pl-5 mt-2'>
              <li>Rent credit will be deducted from your wallet.</li>
              <li>Rent credit will be released if the payment is not completed within an hour.</li>
              <li>
                If payment is made after an hour, only the amount without the rent credit will be
                accepted.
              </li>
            </ul>
          </div>
        )}
        <div className='submitBtn'>
          {loading || (!widgetReady && <ButtonSpinner />)}
          {APP_ENV === 'production' ? (
            <div
              {...({
                alt5: 'Paywidget',
                alt5_id: '2dfa2fcd-2de0-43d9-995b-dc6ff946c028',
                amount: cryptoAmount,
                currency: 'CAD',
                ref_id: cryptoSessionId,
                class: 'alt5paybtn',
              } as any)}
            ></div>
          ) : (
            <div
              {...({
                paydev: 'Paywidget',
                paydev_id: '253c99d5-3389-455c-82e2-e468a7189d2f',
                amount: cryptoAmount,
                currency: 'CAD',
                ref_id: cryptoSessionId,
              } as any)}
            ></div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export { PayWithCrypto };
